import { useState } from "react"

import { useTranslation } from "react-i18next"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

type Sources = ("company_libraries" | "web" | "user_documents")[]
type FileHandling = "context" | "search"
type ReportLength = "short" | "standard" | "extended"
type ResearchDepth = "quick" | "detailed"
type ReportFormat = "structured_summary" | "apa" | "file"

export function DemoResearchSettings() {
  const { t } = useTranslation()
  const [sources, setSources] = useState<Sources>(["company_libraries", "web"])
  const [fileHandling, setFileHandling] = useState<FileHandling>("context")
  const [reportLength, setReportLength] = useState<ReportLength>("short")
  const [researchDepth, setResearchDepth] = useState<ResearchDepth>("detailed")
  const [reportLanguage, setReportLanguage] = useState("Svenska")
  const [reportFormat, setReportFormat] = useState<ReportFormat>("structured_summary")

  const toggleSource = (source: Sources[0]) => {
    if (sources.includes(source)) {
      if (sources.length > 1) {
        setSources(sources.filter((s) => s !== source))
      }
    } else {
      setSources([...sources, source])
    }
  }

  return (
    <div className="bg-sidebar w-full space-y-4 rounded-lg border p-3 sm:max-w-xs sm:p-4">
      <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {t("pages.ai_explorer.scholar_settings")}
      </span>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.sources")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.sources_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <div className="flex flex-col gap-1 pl-2">
          <Label className="flex items-center gap-2">
            <Checkbox
              checked={sources.includes("company_libraries")}
              onCheckedChange={() => toggleSource("company_libraries")}
            />
            <span className="text-sm">{t("pages.ai_explorer.sources_company_libraries")}</span>
          </Label>
          <Label className="flex items-center gap-2">
            <Checkbox
              checked={sources.includes("web")}
              onCheckedChange={() => toggleSource("web")}
            />
            <span className="text-sm">{t("pages.ai_explorer.sources_web")}</span>
          </Label>
          <Label className="flex items-center gap-2">
            <Checkbox
              checked={sources.includes("user_documents")}
              onCheckedChange={() => toggleSource("user_documents")}
            />
            <span className="text-sm">{t("pages.ai_explorer.sources_uploaded_files")}</span>
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.file_handling")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.file_handling_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select value={fileHandling} onValueChange={(v) => setFileHandling(v as FileHandling)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="context">
              {t("pages.ai_explorer.file_handling_use_files_as_context")}
            </SelectItem>
            <SelectItem value="search">
              {t("pages.ai_explorer.file_handling_search_in_files")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.report_length")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_length_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select value={reportLength} onValueChange={(v) => setReportLength(v as ReportLength)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="short">{t("pages.ai_explorer.report_length_short")}</SelectItem>
            <SelectItem value="standard">
              {t("pages.ai_explorer.report_length_standard")}
            </SelectItem>
            <SelectItem value="extended">
              {t("pages.ai_explorer.report_length_extended")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.research_depth")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.research_depth_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select value={researchDepth} onValueChange={(v) => setResearchDepth(v as ResearchDepth)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="quick">{t("pages.ai_explorer.research_depth_quick")}</SelectItem>
            <SelectItem value="detailed">
              {t("pages.ai_explorer.research_depth_detailed")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.report_language")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_language_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select value={reportLanguage} onValueChange={setReportLanguage}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Svenska">Svenska</SelectItem>
            <SelectItem value="English">English</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Tooltip>
          <TooltipTrigger className="w-full text-left">
            <Label>{t("pages.ai_explorer.report_format")}</Label>
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="right">
            <p>{t("pages.ai_explorer.report_format_tooltip")}</p>
          </TooltipContent>
        </Tooltip>
        <Select value={reportFormat} onValueChange={(v) => setReportFormat(v as ReportFormat)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="structured_summary">
              {t("pages.ai_explorer.report_format_structured_summary")}
            </SelectItem>
            <SelectItem value="apa">{t("pages.ai_explorer.report_format_apa")}</SelectItem>
            <SelectItem value="file">{t("pages.ai_explorer.report_format_file")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
